/**
 * The campaign email pace form's rules, free of React so they can be tested alone.
 * Backend reference: docs/CRM_EMAIL_SEND_PACE.md in ilbp-backend.
 */

export interface CrmEmailSendSettings {
  perRun: number;
  setFromDashboard: boolean;
  serverDefault: number;
  min: number;
  max: number;
  sendCron: string;
  sendingEnabled: boolean;
  updatedAt?: string | null;
  warning?: string | null;
}

const WHOLE_NUMBER = /^\d+$/;

/** The sender's default schedule, and the only one a pace can be worked out from here. */
const EVERY_MINUTE = "* * * * *";

/** The size of the list the team sends to, so the estimate is one they recognise. */
const EXAMPLE_AUDIENCE = 42_000;

/** The message to put on the input, or an empty string when the value can be saved. */
export function validatePerRun(text: string, min = 1, max = 5000): string {
  const value = text.trim();
  if (!value) return "Enter a number.";
  if (!WHOLE_NUMBER.test(value)) return "Enter a whole number, for example 100.";

  const perRun = Number(value);
  if (perRun < min || perRun > max) {
    return `The number must be from ${min.toLocaleString()} to ${max.toLocaleString()}.`;
  }

  return "";
}

export interface PerRunEffect {
  severity: "info" | "warning";
  text: string;
}

/** What a number means for a campaign. The cut-off is advice: the server takes the whole range. */
export function describePerRun(perRun: number): PerRunEffect {
  if (perRun > 1000) {
    return {
      severity: "warning",
      text: "Fast. Inbox providers can slow or refuse a sender that jumps in volume, a restart during a run loses more emails, and a run this big may not finish within its minute.",
    };
  }
  return {
    severity: "info",
    text: "A steady pace. Raise it in steps and watch bounces and spam reports on the campaign's report.",
  };
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))} minutes`;

  const hours = Math.round((minutes / 60) * 10) / 10;
  return `${hours} ${hours === 1 ? "hour" : "hours"}`;
}

/** How long a campaign takes at this number, when the schedule says how often runs happen. */
export function describePace(perRun: number, sendCron: string): string {
  if (sendCron !== EVERY_MINUTE) {
    return `This server runs the sender on its own schedule (${sendCron}), so the pace depends on that as well.`;
  }

  const duration = formatDuration(EXAMPLE_AUDIENCE / perRun);
  return `At ${perRun.toLocaleString()} a minute, a campaign to ${EXAMPLE_AUDIENCE.toLocaleString()} people takes about ${duration}.`;
}

export function describeSource(settings: CrmEmailSendSettings): string {
  return settings.setFromDashboard
    ? "Set from this page."
    : "Set by the server's configuration. Saving a number here takes over from it.";
}

export const PROPAGATION_NOTICE =
  "A saved number applies from the sender's next run, within about a minute, including to a campaign already sending. A run under way finishes at its old size. There is no deploy or restart.";
